import { RiskMatrixAxis as RiskMatrixDefinitionAxis, RiskMatrixCell as RiskMatrixDefinitionCell, RiskMatrixDefinition } from 'app/area/case/types-store';
import { CodeListItem } from 'app/area/codetable/list/types-store';
import { RiskMatrixTranslation } from 'app/area/risk-matrix-administration/constants';
import { RiskMatrixAxes, RiskMatrixCellProperty, RiskMatrixCell, RiskMatrixCells, RiskMatrixAxis } from 'app/area/risk-matrix-administration/types-store';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { ComplexValue } from 'ui';

export interface ToRiskMatrixDefinition {
  axes: RiskMatrixAxes;
  cells: RiskMatrixCells;
  riskAreas: CodeListItem[];
  translation: RiskMatrixTranslation;
  thirdAxisItem?: ComplexValue<number>;
  selectedCellId?: number;
}

export const mapToRiskMatrixDefinition = (payload: ToRiskMatrixDefinition): RiskMatrixDefinition => ({
  type: undefined,
  axes: mapAxes(payload.axes, payload.translation),
  cells: mapCells(payload)
});

export const mapToRiskMatrixPredefinedValues = (thirdAxisItem?: ComplexValue<number>): number[] =>
  [undefined, undefined, thirdAxisItem?.value];

export const getAxisDescription = (axisCodetableId: number, translation: RiskMatrixTranslation): string => ({
  130: translation.recurrence_frequency,
  134: translation.severity,
  169: translation.likelihood,
  131: translation.risk_area
})[axisCodetableId];

export const getAxisProperty = (axisCodetableId: number): string => ({
  130: RiskMatrixCellProperty.recurrence,
  134: RiskMatrixCellProperty.severity,
  169: RiskMatrixCellProperty.likelihood
})[axisCodetableId];

const mapAxes = (axes: RiskMatrixAxes, translation: RiskMatrixTranslation): RiskMatrixDefinitionAxis[] =>
  fillMissingAxis(axes?.map((axis, index) => ({
    id: index,
    editable: true,
    fakeAxis: false,
    data: {
      description: getAxisDescription(axis.codetableId, translation),
      codetableId: axis.codetableId,
      options: axis?.items?.map(axisItem => ({ value: axisItem.id, description: axisItem.description })),
      pleaseSelectDescription: undefined
    }
  })) ?? []);

const fillMissingAxis = (axes: RiskMatrixDefinitionAxis[]): RiskMatrixDefinitionAxis[] =>
  axes?.length === 1 ? [...axes, emptyAxis()] : axes;

export const emptyAxis = (): RiskMatrixDefinitionAxis => ({
  id: 1,
  editable: false,
  fakeAxis: true,
  data: {
    description: '',
    codetableId: undefined,
    options: [{
      value: 0,
      description: ''
    }, {
      value: 1,
      description: ''
    }],
    pleaseSelectDescription: undefined
  }
});

const mapCells = (payload: ToRiskMatrixDefinition): RiskMatrixDefinitionCell[] =>
  payload?.cells?.map(mapCell(payload)) ?? [];

const mapCell = ({ axes, riskAreas, selectedCellId }: ToRiskMatrixDefinition) => (cell: RiskMatrixCell): RiskMatrixDefinitionCell => ({
  axisValues: mapCellToAxisValues(cell, axes),
  data: {
    riskArea: {
      description: riskAreas.find(riskArea => riskArea.id === cell.riskArea)?.description,
      value: cell.riskArea,
      backgroundColor: riskAreas.find(riskArea => riskArea.id === cell.riskArea)?.colour,
      foregroundColor: undefined
    },
    weight: cell.riskWeight
  },
  inactive: isDefinedIncludingZero(selectedCellId) ? cell.id !== selectedCellId : false
});

const mapCellToAxisValues = (cell: RiskMatrixCell, axes: RiskMatrixAxes): ComplexValue<number>[] => axes.map(mapCellToAxisValue(cell));

const mapCellToAxisValue = (cell: RiskMatrixCell) => (axis: RiskMatrixAxis): ComplexValue<number> => ({
  value: cell[getAxisProperty(axis.codetableId)],
  description: axis.items.find(item => item.id === cell[getAxisProperty(axis.codetableId)])?.description
});
